import { cloneDeep, defaultsDeep } from 'lodash'
import { CatalogLocales } from '../../../models/util.model'
import { CATALOG_DEFAULT_CODE } from '../consts/catalog.const'
import { LOCALE_DEFAULT_CODE } from '../../../consts/locale.const'
import { Catalog } from '../models/catalog.entity'

// Initial states

const CATALOG_INITIAL_STATE: Partial<Catalog> = {
  name: {},
  code: undefined,
  locales: [],
}

/**
 * Initialize a partial catalog
 * @param catalog - the partial catalog
 * @returns the catalog updated
 */
export function initCatalog(catalog: Partial<Catalog> = {}): Catalog {
  return defaultsDeep(cloneDeep(catalog), CATALOG_INITIAL_STATE)
}

/**
 * Get a catalogable value
 * @param catalogValues - the catalog values
 * @param catalogCode - the catalog code
 * @param locale - the locale code
 * @returns the value
 */
export function getCatalogValue<T>(
  catalogValues: CatalogLocales<T>[] | undefined,
  catalogCode = CATALOG_DEFAULT_CODE,
  locale = LOCALE_DEFAULT_CODE,
): T | undefined {
  if (!catalogValues) {
    return undefined
  }

  if (typeof catalogValues === 'string') {
    return catalogValues
  }

  const catalogValue = catalogValues.find(
    (cat) => cat.catalogCode === catalogCode,
  )
  return catalogValue && catalogValue.locales
    ? catalogValue.locales[locale]
    : undefined
}

/**
 * Set a catalogable value
 * @param catalogValues - the catalog values
 * @param value - the value to set
 * @param catalogCode - the catalog code
 * @param locale - the locale code
 * @returns the catalogable value updated
 */
export function setCatalogValue<T>(
  catalogValues: CatalogLocales<T>[],
  value: T,
  catalogCode = CATALOG_DEFAULT_CODE,
  locale = LOCALE_DEFAULT_CODE,
): CatalogLocales<T>[] {
  return [
    ...catalogValues.map((cat) =>
      cat.catalogCode === catalogCode
        ? {
            ...cat,
            locales: { ...cat.locales, [locale]: value },
          }
        : cat,
    ),
  ]
}
